import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import {
    FaQrcode,
    FaSearch,
    FaPrint,
    FaExternalLinkAlt,
    FaCheckCircle,
    FaCircle,
    FaUser,
    FaArrowLeft,
    FaSpinner,
    FaCheckSquare,
    FaSquare
} from 'react-icons/fa';

const AdminAllQrsPage = () => {
    const navigate = useNavigate();
    const [qrCodes, setQrCodes] = useState([]);
    const [usersMap, setUsersMap] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'linked', 'unlinked'
    const [selectedCodes, setSelectedCodes] = useState(new Set());

    const fetchQrsAndUsers = useCallback(async () => {
        setLoading(true);
        try {
            // Fetch QRs
            const querySnapshot = await getDocs(collection(db, 'qrCodes'));
            const qrData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Fetch users map for friendly names
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const uMap = {};
            usersSnapshot.docs.forEach(doc => {
                uMap[doc.id] = doc.data();
            });
            setUsersMap(uMap);

            // Sort by createdAt descending if exists
            qrData.sort((a, b) => {
                const timeA = a.createdAt?.seconds || 0;
                const timeB = b.createdAt?.seconds || 0;
                return timeB - timeA;
            });
            setQrCodes(qrData);
        } catch (error) {
            console.error('QR kodlar alınırken hata:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQrsAndUsers();
    }, [fetchQrsAndUsers]);

    // Filter QRs based on status & search
    const filteredQrs = useMemo(() => {
        return qrCodes.filter(qr => {
            if (filterStatus === 'linked' && !qr.isLinked) return false;
            if (filterStatus === 'unlinked' && qr.isLinked) return false;

            if (!searchQuery.trim()) return true;
            const q = searchQuery.toLowerCase();
            const id = (qr.id || '').toLowerCase();
            const linkedId = (qr.linkedUserId || '').toLowerCase();
            const userName = (usersMap[qr.linkedUserId]?.displayName || '').toLowerCase();
            const userEmail = (usersMap[qr.linkedUserId]?.email || '').toLowerCase();

            return id.includes(q) || linkedId.includes(q) || userName.includes(q) || userEmail.includes(q);
        });
    }, [qrCodes, filterStatus, searchQuery, usersMap]);

    // Checkbox selection handlers
    const toggleSelectCode = (id) => {
        const next = new Set(selectedCodes);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedCodes(next);
    };

    const handleSelectAll = () => {
        if (selectedCodes.size === filteredQrs.length && filteredQrs.length > 0) {
            setSelectedCodes(new Set());
        } else {
            const allIds = new Set(filteredQrs.map(q => q.id));
            setSelectedCodes(allIds);
        }
    };

    // Navigate to Print Page with selected codes
    const handlePrintSelected = () => {
        if (selectedCodes.size === 0) return;
        const codeList = Array.from(selectedCodes).join(',');
        navigate(`/admin/print-qr?selectedCodes=${encodeURIComponent(codeList)}`);
    };

    const stats = useMemo(() => {
        const total = qrCodes.length;
        const linked = qrCodes.filter(q => q.isLinked).length;
        const unlinked = qrCodes.filter(q => !q.isLinked).length;
        return { total, linked, unlinked };
    }, [qrCodes]);

    const isAllSelected = filteredQrs.length > 0 && selectedCodes.size === filteredQrs.length;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-3">
                        <Link
                            to="/admin"
                            className="p-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black transition-colors"
                            title="Admin Paneline Dön"
                        >
                            <FaArrowLeft size={16} />
                        </Link>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <FaQrcode className="text-rose-600" />
                                <span>Tüm QR Kodlar ({qrCodes.length})</span>
                            </h1>
                            <p className="text-xs text-gray-500 mt-0.5">
                                Üretilmiş tüm QR kodlarını görüntüleyin, filtreleyin veya seçerek sticker baskısı alın.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {selectedCodes.size > 0 && (
                            <button
                                type="button"
                                onClick={handlePrintSelected}
                                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md transition-all animate-pulse"
                            >
                                <FaPrint size={14} />
                                <span>Seçilenleri Sticker Bas ({selectedCodes.size} Adet)</span>
                            </button>
                        )}
                        <Link
                            to="/admin/print-qr"
                            className="px-4 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold flex items-center gap-2 transition-colors shadow-sm"
                        >
                            <FaPrint size={12} />
                            <span>Sticker PDF Sayfasına Git</span>
                        </Link>
                    </div>
                </div>

                {/* Metrics Stats Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button
                        onClick={() => setFilterStatus('all')}
                        className={`p-4 rounded-xl border text-left transition-all ${
                            filterStatus === 'all'
                                ? 'bg-rose-50 border-rose-300 ring-2 ring-rose-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500">Toplam QR Kodu</span>
                        <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
                    </button>

                    <button
                        onClick={() => setFilterStatus('linked')}
                        className={`p-4 rounded-xl border text-left transition-all ${
                            filterStatus === 'linked'
                                ? 'bg-green-50 border-green-300 ring-2 ring-green-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <FaCheckCircle className="text-green-500" /> Bir Kullanıcıya Bağlı
                        </span>
                        <p className="text-2xl font-bold text-green-600 mt-1">{stats.linked}</p>
                    </button>

                    <button
                        onClick={() => setFilterStatus('unlinked')}
                        className={`p-4 rounded-xl border text-left transition-all ${
                            filterStatus === 'unlinked'
                                ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-500 ring-offset-1'
                                : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                    >
                        <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                            <FaCircle className="text-amber-500" /> Boşta (Henüz Eşleşmedi)
                        </span>
                        <p className="text-2xl font-bold text-amber-600 mt-1">{stats.unlinked}</p>
                    </button>
                </div>

                {/* Filter and Selection Action Bar */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="relative w-full md:w-96">
                        <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="QR Kodu veya Kullanıcı ile ara..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-rose-500 focus:outline-none"
                        />
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
                        <div className="flex items-center gap-1.5">
                            {[
                                { key: 'all', label: 'Tümü' },
                                { key: 'unlinked', label: 'Boştakiler' },
                                { key: 'linked', label: 'Bağlı Olanlar' }
                            ].map(tab => (
                                <button
                                    key={tab.key}
                                    onClick={() => setFilterStatus(tab.key)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                                        filterStatus === tab.key
                                            ? 'bg-black text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Select All Checkbox */}
                        <button
                            type="button"
                            onClick={handleSelectAll}
                            className="px-3 py-1.5 rounded-xl border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100 flex items-center gap-1.5"
                        >
                            {isAllSelected ? <FaCheckSquare className="text-blue-600" /> : <FaSquare className="text-gray-400" />}
                            <span>{isAllSelected ? 'Seçimi Kaldır' : 'Tümünü Seç'}</span>
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="py-20 text-center text-gray-500">
                            <FaSpinner className="animate-spin text-3xl text-rose-600 mx-auto mb-3" />
                            <p className="text-sm">QR Kodlar yükleniyor...</p>
                        </div>
                    ) : filteredQrs.length === 0 ? (
                        <div className="py-20 text-center text-gray-500">
                            <FaQrcode className="text-4xl text-gray-300 mx-auto mb-2" />
                            <p className="font-semibold text-gray-700">QR Kodu bulunamadı</p>
                            <p className="text-xs text-gray-400 mt-1">Arama kriterlerinizi değiştirmeyi deneyin.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr className="bg-gray-50/80 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                        <th className="px-4 py-4 w-12 text-center">
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            />
                                        </th>
                                        <th className="px-6 py-4">QR Kodu</th>
                                        <th className="px-6 py-4">Durum</th>
                                        <th className="px-6 py-4">Bağlı Kullanıcı</th>
                                        <th className="px-6 py-4">Oluşturulma Tarihi</th>
                                        <th className="px-6 py-4 text-right">İşlem</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-sm">
                                    {filteredQrs.map((qr) => {
                                        const isSelected = selectedCodes.has(qr.id);
                                        const linkedUser = qr.linkedUserId ? usersMap[qr.linkedUserId] : null;

                                        return (
                                            <tr
                                                key={qr.id}
                                                className={`transition-colors ${
                                                    isSelected ? 'bg-blue-50/60' : 'hover:bg-gray-50/60'
                                                }`}
                                            >
                                                {/* Checkbox */}
                                                <td className="px-4 py-4 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelectCode(qr.id)}
                                                        className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                </td>

                                                {/* QR ID */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center space-x-2">
                                                        <span className="font-mono font-bold text-gray-900 bg-gray-100 px-2 py-1 rounded">
                                                            {qr.id}
                                                        </span>
                                                        <a
                                                            href={`https://kodutara.web.app/q/${qr.id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                                                            title="QR Taramasını Test Et"
                                                        >
                                                            <FaExternalLinkAlt size={12} />
                                                        </a>
                                                    </div>
                                                </td>

                                                {/* Status */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {qr.isLinked ? (
                                                        <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full inline-flex items-center gap-1">
                                                            <FaCheckCircle size={10} /> Bağlı
                                                        </span>
                                                    ) : (
                                                        <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full inline-flex items-center gap-1">
                                                            <FaCircle size={8} /> Boşta
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Linked User */}
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {qr.isLinked && qr.linkedUserId ? (
                                                        <Link
                                                            to={`/admin/user/${qr.linkedUserId}`}
                                                            className="flex items-center gap-2 group"
                                                            title="Kullanıcı Yönetim Sayfasına Git"
                                                        >
                                                            <div className="w-7 h-7 rounded-lg bg-gray-200 text-gray-700 font-bold text-xs flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                                                <FaUser size={10} />
                                                            </div>
                                                            <div>
                                                                <p className="font-semibold text-gray-800 text-xs group-hover:text-blue-600 transition-colors">
                                                                    {linkedUser?.displayName || 'İsimsiz'}
                                                                </p>
                                                                <p className="text-[11px] text-gray-400">
                                                                    {linkedUser?.email || qr.linkedUserId}
                                                                </p>
                                                            </div>
                                                        </Link>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs italic">— Boşta —</span>
                                                    )}
                                                </td>

                                                {/* Date */}
                                                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                                                    {qr.createdAt?.seconds
                                                        ? new Date(qr.createdAt.seconds * 1000).toLocaleDateString('tr-TR')
                                                        : '—'}
                                                </td>

                                                {/* Actions */}
                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/admin/print-qr?selectedCodes=${qr.id}`)}
                                                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                                                    >
                                                        <FaPrint size={10} />
                                                        <span>Baskı Al</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default AdminAllQrsPage;